// Every BL+ API response is wrapped as { payload, errors, info, warnings, links }.
// This wrapper is the ONE place that unwraps it — every call site gets back
// just the payload, or a thrown ApiError. Never fetch() this API directly
// from a component; always go through this file (see Issue #1).

const API_BASE = import.meta.env.VITE_API_BASE;

// CSRF gate for the cookie-mode refresh path (docs/admin-ui-integration.md §1b).
// The browser auto-attaches the HttpOnly refresh cookie to any POST, but it
// never auto-attaches a custom header to a cross-site form post — so requiring
// this header's presence (any non-empty value) is what proves the request was
// made by our own script and not by a hostile page spending the cookie.
// The API admits this header in CORS preflights for allow-listed origins only.
export const CSRF_HEADER_NAME = "x-blplus-csrf";

// Mutating verbs drive the cookie path (/auth/refresh, /auth/logout), so they
// always carry the CSRF header. Reads are excluded — the header is a signal
// about auto-attached credentials, which browsers only attach to unsafe
// methods in the first place.
const CSRF_HTTP_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  status: number;
  errors: (string | Record<string, unknown>)[];

  constructor(
    message: string,
    status: number,
    errors: (string | Record<string, unknown>)[] = []
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }

  // The 401s on the cookie path (/auth/refresh) carry a machine-readable
  // `code` in the errors envelope instead of a plain string:
  //   no_refresh_cookie       — no cookie was ever set (or an old browser
  //                             without cookie partitioning dropped it)
  //   invalid_refresh_token   — a cookie arrived but the token is dead
  //   csrf_header_missing     — request was not made by our own script
  // `no_refresh_cookie` on mount simply means "not signed in yet"; the other
  // two mean a session existed and is gone. Both end at the login screen.
  get errorCode(): string | null {
    const first = this.errors[0];
    if (first && typeof first === "object" && "code" in first) {
      return String(first.code);
    }
    return null;
  }
}

interface Envelope<T> {
  payload: T;
  errors: (string | Record<string, unknown>)[];
  info: string[];
  warnings: string[];
  links: unknown[];
}

// Access token lives in module state (memory only). The REFRESH token is never
// held here: since the API's cookie-session change it arrives only as a
// partitioned HttpOnly cookie (blplus_rt) the browser stores and re-sends by
// itself, so it is unreadable to this code — which is the point. The access
// token is short-lived, so losing it on a hard reload is fine: AuthProvider
// re-establishes the session via POST /auth/refresh on mount.
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function getAuthToken() {
  return authToken;
}

// AuthProvider registers this so an expired/revoked token mid-session sends
// the user back to /login instead of leaving every query failing.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  body?: unknown; // pass a plain object; it's JSON.stringify'd for you
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  // credentials: "include" is required — the session's refresh token lives in
  // the HttpOnly cookie (blplus_rt), so the browser must be allowed to store
  // and re-send it. The API grants credentials (and admits the CSRF header)
  // only to origins in its ADMIN_UI_ALLOWED_ORIGINS; any other origin gets no
  // Access-Control-Allow-Origin and the browser blocks the response.
  if (CSRF_HTTP_METHODS.has((options.method ?? "GET").toUpperCase())) {
    headers[CSRF_HEADER_NAME] = "1";
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: "include",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch (err) {
    if (options.signal?.aborted) throw err;
    throw new ApiError(
      "Can't reach the API. It may be down, or this site's origin isn't in the API's ADMIN_UI_ALLOWED_ORIGINS.",
      0,
      []
    );
  }

  if (response.status === 401) {
    const hadToken = authToken !== null;
    setAuthToken(null);
    if (hadToken) onUnauthorized?.();
    // Let the caller/UI react (redirect to /login); we don't force a
    // navigation from inside the client itself.
    throw new ApiError("Not authenticated", 401, ["Not authenticated"]);
  }

  // The backend can return errors inside a 200 envelope, so we always try to
  // parse JSON and check `.errors` rather than trusting response.ok alone.
  let json: Envelope<T>;
  try {
    json = await response.json();
  } catch {
    throw new ApiError(
      `Request to ${path} failed with status ${response.status} and a non-JSON body`,
      response.status,
      []
    );
  }

  if (!response.ok || (json.errors && json.errors.length > 0)) {
    const first = json.errors?.[0];
    const message =
      typeof first === "string" ? first : `Request to ${path} failed`;
    throw new ApiError(message, response.status, json.errors ?? []);
  }

  return json.payload;
}

// For endpoints that return a file (e.g. CSV exports) instead of the JSON
// envelope. Errors still come back enveloped, so they're unwrapped the same way.
export async function apiDownload(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      headers,
    });
  } catch {
    throw new ApiError("Can't reach the API.", 0, []);
  }
  if (response.status === 401) {
    const hadToken = authToken !== null;
    setAuthToken(null);
    if (hadToken) onUnauthorized?.();
    throw new ApiError("Not authenticated", 401, ["Not authenticated"]);
  }
  if (!response.ok) {
    const errors = await response
      .json()
      .then((j: Envelope<unknown>) => j.errors ?? [])
      .catch(() => []);
    const first = errors[0];
    throw new ApiError(
      (typeof first === "string" ? first : null) ?? `Download of ${path} failed`,
      response.status,
      errors
    );
  }
  return response.blob();
}

// Escape hatch for the auth bootstrap only. apiFetch turns every 401 into a
// bare "Not authenticated", but the rehydrate probe MUST read the coded body
// to tell `no_refresh_cookie` (never signed in) from a dead session — both end
// at the login screen, but only this way the UI can distinguish them in logs
// and tests. Also skips the Authorization header: on a cold mount there is no
// access token, and sending a stale one would just be noise.
//
// export-intentional: this is for AuthProvider's session bootstrap, not for
// feature code — feature calls keep going through apiFetch.
export async function authProbe<T>(path: string): Promise<{ status: number; json: Envelope<T> | null }> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { [CSRF_HEADER_NAME]: "1" },
    });
  } catch {
    throw new ApiError(
      "Can't reach the API. It may be down, or this site's origin isn't in the API's ADMIN_UI_ALLOWED_ORIGINS.",
      0,
      []
    );
  }
  let json: Envelope<T> | null = null;
  try {
    json = (await response.json()) as Envelope<T>;
  } catch {
    // A 401 with a non-JSON body is still a definitive answer; surface status.
  }
  return { status: response.status, json };
}
